/**
 * Markdown Library - SQL Highlighter
 * 
 * Syntax highlighting for SQL code blocks.
 */

import React from 'react';
import {
    SQL_KEYWORDS,
    SQL_FUNCTIONS,
    SQL_OPERATORS,
    SQL_CONDITIONALS,
    createWordBoundaryPattern,
} from './patterns';

// Pre-compiled regex patterns for performance
const PATTERNS = {
    comments: /(--[^\n]*)/g,
    strings: /'([^']*?)'|"([^"]*?)"/g,
    numbers: /\b(\d+(\.\d+)?)\b/g,
    keywords: createWordBoundaryPattern(SQL_KEYWORDS),
    functions: createWordBoundaryPattern(SQL_FUNCTIONS),
    operators: createWordBoundaryPattern(SQL_OPERATORS),
    conditionals: createWordBoundaryPattern(SQL_CONDITIONALS),
    comparison: /\b(=|!=|<>|<|>|<=|>=)\b/g,
};

// CSS classes for each token type
const TOKEN_CLASSES = {
    comment: 'text-slate-400',
    string: 'text-accent',
    number: 'text-yellow-400',
    keyword: 'text-fuchsia-400 font-semibold',
    function: 'text-cyan-400 font-semibold',
    operator: 'text-orange-400',
    conditional: 'text-purple-400 font-semibold',
};

/**
 * Highlights SQL syntax by wrapping tokens in styled spans.
 * 
 * @param sql - Raw SQL string
 * @returns React element with highlighted SQL
 */
export function highlightSql(sql: string): React.ReactNode {
    let result = sql;

    // Order matters: replace in order of specificity

    // 1. Comments (highest priority - don't highlight inside comments)
    result = result.replace(
        PATTERNS.comments,
        `<span class="${TOKEN_CLASSES.comment}">$1</span>`
    );

    // 2. Strings
    result = result.replace(
        PATTERNS.strings,
        (match) => `<span class="${TOKEN_CLASSES.string}">${match}</span>`
    );

    // 3. Numbers
    result = result.replace(
        PATTERNS.numbers,
        `<span class="${TOKEN_CLASSES.number}">$1</span>`
    );

    // 4. SQL Keywords
    result = result.replace(
        PATTERNS.keywords,
        `<span class="${TOKEN_CLASSES.keyword}">$1</span>`
    );

    // 5. Functions
    result = result.replace(
        PATTERNS.functions,
        `<span class="${TOKEN_CLASSES.function}">$1</span>`
    );

    // 6. Operators
    result = result.replace(
        PATTERNS.operators,
        `<span class="${TOKEN_CLASSES.operator}">$1</span>`
    );

    // 7. Conditionals
    result = result.replace(
        PATTERNS.conditionals,
        `<span class="${TOKEN_CLASSES.conditional}">$1</span>`
    );

    // 8. Comparison operators
    result = result.replace(
        PATTERNS.comparison,
        (match) => `<span class="${TOKEN_CLASSES.operator}">${match}</span>`
    );

    return (
        <span
            className="text-white"
            dangerouslySetInnerHTML={{ __html: result }}
        />
    );
}
